import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import StickyLanguageSelector from '../../components/StickyLanguageSelector'
import SimpleSiteImageUpload from '../../components/SimpleSiteImageUpload'
import { getAll, putOne } from '../../services/common_requests'
import { KTCardBody } from '../../../_dashboard/helpers'
import { MENU_NAV_LANGUAGES } from '../header-management/menuNavigationUtils'
import { MenuItem, displayLabel } from '../header-management/menuNavigationUtils'

const SHARE_BUTTON_PLATFORMS = ['facebook', 'whatsapp', 'copy', 'native'] as const

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

function readInitialLang(): string {
  try {
    const ls = localStorage.getItem(I18N_CONFIG_KEY) || '{"selectedLang":"fr"}'
    const code = JSON.parse(ls).selectedLang as string
    return (MENU_NAV_LANGUAGES as readonly string[]).includes(code) ? code : 'en'
  } catch {
    return 'en'
  }
}

function emptyLangMap(): Record<string, string> {
  return { en: '', fr: '', de: '' }
}

function mergeLangRecord(
  api: Record<string, string> | undefined | null,
): Record<string, string> {
  const o = emptyLangMap()
  if (!api) return o
  for (const k of MENU_NAV_LANGUAGES) {
    if (api[k] != null) o[k] = api[k]
  }
  return o
}

function extractStandardData(res: any): any {
  return res?.data ?? null
}

function extractMenuList(res: any): MenuItem[] {
  if (!res) return []
  if (Array.isArray(res.data)) return res.data as MenuItem[]
  return []
}

function imageUrlForDisplay(url: string | undefined): string {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return `${process.env.REACT_APP_API_URL_IMAGE || ''}${url}`
}

const FooterManagement: React.FC = () => {
  const intl = useIntl()
  const [selectedLanguage, setSelectedLanguage] = useState(readInitialLang)
  const [loading, setLoading] = useState(true)
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [saving, setSaving] = useState(false)

  const loadMenuPreview = useCallback(async () => {
    try {
      const res = await getAll({}, '/menu')
      setMenuItems(extractMenuList(res))
    } catch {
      setMenuItems([])
    }
  }, [])

  const validationSchema = useMemo(
    () =>
      Yup.object({
        footerLogoId: Yup.string(),
      }),
    [],
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      footerLogoId: '',
      footerLogoPreview: '',
      footerDescription: emptyLangMap(),
      newsletterTitle: emptyLangMap(),
      newsletterSubtitle: emptyLangMap(),
      shareEnabledPlatforms: [...SHARE_BUTTON_PLATFORMS] as string[],
      shareUtmQuery: '',
      tripAdvisorWidgetHtml: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setSaving(true)
      try {
        const res = await putOne(
          {
            footerLogo: values.footerLogoId || undefined,
            footerDescription: values.footerDescription,
            newsletterTitle: values.newsletterTitle,
            newsletterSubtitle: values.newsletterSubtitle,
            shareEnabledPlatforms: values.shareEnabledPlatforms,
            shareUtmQuery: values.shareUtmQuery?.trim() || undefined,
            tripAdvisorWidgetHtml: values.tripAdvisorWidgetHtml?.trim() || undefined,
          },
          '/site-settings/footer',
        )
        if ((res as any)?.payload?.code === 3013) {
          toast.success(
            intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SUCCESS.SAVED' }),
            { position: 'top-right' },
          )
        } else {
          toast.error(intl.formatMessage({ id: 'FOOTER_MANAGEMENT.ERROR.SAVE' }))
        }
      } catch (e) {
        toast.error(intl.formatMessage({ id: 'FOOTER_MANAGEMENT.ERROR.SAVE' }))
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await getAll({}, '/site-settings/footer')
        const d = extractStandardData(res)
        if (!d || cancelled) return
        const logo = d.footerLogo
        const logoId =
          typeof logo === 'object' && logo?._id
            ? String(logo._id)
            : logo
              ? String(logo)
              : ''
        const logoUrl =
          typeof logo === 'object' && logo?.url ? String(logo.url) : ''
        formik.setValues({
          footerLogoId: logoId,
          footerLogoPreview: logoUrl,
          footerDescription: mergeLangRecord(d.footerDescription),
          newsletterTitle: mergeLangRecord(d.newsletterTitle),
          newsletterSubtitle: mergeLangRecord(d.newsletterSubtitle),
          shareEnabledPlatforms:
            Array.isArray(d.shareEnabledPlatforms) && d.shareEnabledPlatforms.length > 0
              ? d.shareEnabledPlatforms.map(String)
              : [...SHARE_BUTTON_PLATFORMS],
          shareUtmQuery: typeof d.shareUtmQuery === 'string' ? d.shareUtmQuery : '',
          tripAdvisorWidgetHtml:
            typeof d.tripAdvisorWidgetHtml === 'string' ? d.tripAdvisorWidgetHtml : '',
        })
      } catch (e) {
        toast.error(intl.formatMessage({ id: 'FOOTER_MANAGEMENT.ERROR.LOAD' }))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [])

  useEffect(() => {
    loadMenuPreview()
  }, [loadMenuPreview])

  const footerCols = ['footer_col1', 'footer_col2', 'footer_col3'] as const

  const previewForCol = (loc: string) =>
    menuItems
      .filter((m) => m.location === loc && !m.parent)
      .sort((a, b) => a.position - b.position)

  if (loading) {
    return (
      <KTCardBody className='py-15 text-center'>
        <Spinner animation='border' className='text-primary' />
      </KTCardBody>
    )
  }

  return (
    <KTCardBody className='py-6 px-4 px-lg-8'>
      <div className='d-flex flex-column mb-8'>
        <h1 className='fs-2x fw-bold text-gray-900 mb-1'>
          {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.PAGE_TITLE' })}
        </h1>
        <p className='text-gray-500 fs-6 mb-0'>
          {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.PAGE_SUBTITLE' })}
        </p>
      </div>

      <StickyLanguageSelector
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
        allowedLanguageCodes={[...MENU_NAV_LANGUAGES]}
      />

      <form onSubmit={formik.handleSubmit}>
        <div className='alert alert-info d-flex align-items-start gap-3 mb-6' role='alert'>
          <i className='bi bi-info-circle-fill fs-3 text-info mt-1' />
          <div>
            <strong>
              {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SOCIAL_MOVED.TITLE' })}
            </strong>
            <div className='fs-7 mt-1'>
              {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SOCIAL_MOVED.BODY' })}
            </div>
          </div>
        </div>
        <div className='card card-flush shadow-sm mb-6'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title align-items-start flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SECTION.GENERAL' })}
              </span>
              <span className='text-gray-400 mt-1 fw-semibold fs-6'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SECTION.GENERAL_HINT' })}
              </span>
            </div>
          </div>
          <div className='card-body pt-0 pb-6 px-6'>
            <Row className='g-6'>
              <Col md={4}>
                <label className='form-label fw-semibold text-gray-700'>
                  {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.FOOTER_LOGO' })}
                </label>
                <div
                  className={clsx(
                    'border border-2 border-dashed border-gray-300 rounded-3 p-6 p-md-8 text-center bg-light-primary bg-opacity-5 hover-elevate-up transition-all',
                  )}
                  style={{ cursor: 'default' }}
                >
                  {formik.values.footerLogoPreview ? (
                    <img
                      src={imageUrlForDisplay(formik.values.footerLogoPreview)}
                      alt=''
                      className='mw-100 rounded mb-4 shadow-sm'
                      style={{ maxHeight: 140 }}
                    />
                  ) : (
                    <div className='mb-4'>
                      <i className='bi bi-cloud-arrow-up fs-2x text-primary mb-3 d-block' />
                      <span className='text-gray-600 fs-7'>
                        {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.LOGO_DROP_HINT' })}
                      </span>
                    </div>
                  )}
                  <SimpleSiteImageUpload
                    onImageAdded={({ _id, url }) => {
                      formik.setFieldValue('footerLogoId', _id)
                      formik.setFieldValue('footerLogoPreview', url)
                    }}
                  />
                </div>
              </Col>
              <Col md={8}>
                <Form.Group className='mb-6'>
                  <Form.Label className='fw-semibold text-gray-700'>
                    {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.DESCRIPTION' })}{' '}
                    <span className='badge badge-light-primary ms-1'>
                      {selectedLanguage.toUpperCase()}
                    </span>
                  </Form.Label>
                  <div className='rounded border border-gray-200 overflow-hidden'>
                    <ReactQuill
                      theme='snow'
                      value={formik.values.footerDescription[selectedLanguage] || ''}
                      onChange={(html) =>
                        formik.setFieldValue(`footerDescription.${selectedLanguage}`, html)
                      }
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        <div className='card card-flush shadow-sm mb-6'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title align-items-start flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SECTION.SHARE' })}
              </span>
              <span className='text-gray-400 mt-1 fw-semibold fs-6'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SHARE.HINT' })}
              </span>
            </div>
          </div>
          <div className='card-body pt-0 pb-6 px-6'>
            <div className='d-flex flex-wrap gap-4 mb-6'>
              {SHARE_BUTTON_PLATFORMS.map((p) => (
                <Form.Check
                  key={p}
                  type='checkbox'
                  id={`share-platform-${p}`}
                  label={p}
                  checked={formik.values.shareEnabledPlatforms.includes(p)}
                  onChange={(e) => {
                    const cur = formik.values.shareEnabledPlatforms
                    if (e.target.checked) {
                      formik.setFieldValue('shareEnabledPlatforms', [...cur, p])
                    } else {
                      formik.setFieldValue(
                        'shareEnabledPlatforms',
                        cur.filter((x) => x !== p),
                      )
                    }
                  }}
                />
              ))}
            </div>
            <Form.Group className='mb-0'>
              <Form.Label className='fw-semibold text-gray-700'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SHARE.UTM' })}
              </Form.Label>
              <Form.Control
                className='form-control-solid'
                placeholder='utm_source=share&utm_medium=social'
                value={formik.values.shareUtmQuery}
                onChange={(e) => formik.setFieldValue('shareUtmQuery', e.target.value)}
              />
              <div className='form-text'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SHARE.UTM_HINT' })}
              </div>
            </Form.Group>
          </div>
        </div>

        <div className='card card-flush shadow-sm mb-6'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title align-items-start flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SECTION.NEWSLETTER' })}
              </span>
            </div>
          </div>
          <div className='card-body pt-0 pb-6 px-6'>
            <Form.Group className='mb-6'>
              <Form.Label className='fw-semibold text-gray-700'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.NEWSLETTER.TITLE' })}{' '}
                <span className='badge badge-light-primary ms-1'>
                  {selectedLanguage.toUpperCase()}
                </span>
              </Form.Label>
              <Form.Control
                className='form-control-solid'
                value={formik.values.newsletterTitle[selectedLanguage] || ''}
                onChange={(e) =>
                  formik.setFieldValue(`newsletterTitle.${selectedLanguage}`, e.target.value)
                }
              />
            </Form.Group>
            <Form.Group className='mb-0'>
              <Form.Label className='fw-semibold text-gray-700'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.NEWSLETTER.SUBTITLE' })}{' '}
                <span className='badge badge-light-primary ms-1'>
                  {selectedLanguage.toUpperCase()}
                </span>
              </Form.Label>
              <Form.Control
                className='form-control-solid'
                value={formik.values.newsletterSubtitle[selectedLanguage] || ''}
                onChange={(e) =>
                  formik.setFieldValue(`newsletterSubtitle.${selectedLanguage}`, e.target.value)
                }
              />
            </Form.Group>
          </div>
        </div>

        <div className='card card-flush shadow-sm mb-6'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title align-items-start flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>TripAdvisor widget</span>
              <span className='text-gray-400 mt-1 fw-semibold fs-6'>
                Paste the official TripAdvisor embed HTML snippet (footer).
              </span>
            </div>
          </div>
          <div className='card-body pt-0 pb-6 px-6'>
            <Form.Control
              as='textarea'
              rows={5}
              className='form-control-solid font-monospace'
              value={formik.values.tripAdvisorWidgetHtml}
              onChange={(e) => formik.setFieldValue('tripAdvisorWidgetHtml', e.target.value)}
              placeholder='<div id="TA_selfserveprop"...></div>'
            />
          </div>
        </div>

        <div className='card card-flush shadow-sm mb-6'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title align-items-start flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SECTION.LINKS' })}
              </span>
              <span className='text-gray-400 mt-1 fw-semibold fs-6'>
                {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SECTION.LINKS_HINT' })}
              </span>
            </div>
          </div>
          <div className='card-body pt-0 pb-6 px-6'>
            <Row className='g-6'>
              {footerCols.map((c) => (
                <Col md={4} key={c}>
                  <div className='border border-gray-200 border-dashed rounded-3 p-4 h-100 bg-light'>
                    <h6 className='fw-bold text-gray-800 mb-3'>
                      {intl.formatMessage({ id: `MENU_NAVIGATION.LOCATION.${c}` })}
                    </h6>
                    <ul className='list-unstyled small ps-0 mb-4'>
                      {previewForCol(c).length === 0 && (
                        <li className='text-gray-500 fs-7'>
                          {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.LINKS.EMPTY' })}
                        </li>
                      )}
                      {previewForCol(c).map((m) => (
                        <li key={m._id} className='mb-2'>
                          <span className='badge badge-light-primary fw-semibold me-2'>
                            {displayLabel(m, selectedLanguage)}
                          </span>
                          <span className='text-muted fs-8 d-block'>{m.url}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/apps/header-management/header?location=${c}`}
                      className='btn btn-sm btn-light-primary rounded-pill'
                    >
                      <i className='bi bi-pencil-square me-1' />
                      {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.LINKS.EDIT' })}
                    </Link>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        <div className='d-flex flex-wrap justify-content-end gap-3 mt-3'>
          <Button
            type='button'
            variant='light'
            className='rounded-pill px-5'
            onClick={() => loadMenuPreview()}
          >
            <i className='bi bi-arrow-clockwise me-2' />
            {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.REFRESH_PREVIEW' })}
          </Button>
          <Button
            type='submit'
            variant='primary'
            className='px-8 py-3 rounded-pill shadow-sm d-flex align-items-center gap-2'
            disabled={saving}
          >
            {saving && <Spinner animation='border' size='sm' />}
            <i className='bi bi-check-lg' />
            {intl.formatMessage({ id: 'FOOTER_MANAGEMENT.SAVE' })}
          </Button>
        </div>
      </form>
    </KTCardBody>
  )
}

export default FooterManagement
